/* HN image menu. Enhances existing Shop links; removing this section restores them.
   No cart, product, shared navigation, or core Dawn files are changed. */
(() => {
  if (customElements.get('hn-image-menu')) return;
  class HNImageMenu extends HTMLElement {
    connectedCallback() {
      this.mounts = [];
      this.abort = new AbortController();
      this.media = matchMedia('(min-width: 990px)');
      this.hoverMedia = matchMedia('(hover: hover) and (pointer: fine)');
      const options = { signal: this.abort.signal };
      this.frame = requestAnimationFrame(() => this.mount());
      document.addEventListener('shopify:section:load', event => {
        if (event.target.matches('.section-header') || event.target.querySelector('.section-header')) this.mount();
      }, options);
      document.addEventListener('pointerdown', event => {
        this.mounts.forEach(m => { if (!m.root.contains(event.target)) this.close(m); });
      }, options);
      document.addEventListener('open', event => {
        if (event.target.matches('shopify-account')) this.mounts.forEach(m => this.close(m));
      }, { ...options, capture: true });
      document.addEventListener('shopify:section:select', event => {
        if (event.detail.sectionId === this.dataset.section) this.revealInEditor();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        if (event.detail.sectionId === this.dataset.section) this.revealInEditor();
      }, options);
      this.media.addEventListener('change', () => this.mounts.forEach(m => this.close(m)), options);
      window.addEventListener('resize', () => this.positionOpen(), options);
      window.addEventListener('scroll', () => this.positionOpen(), { ...options, passive: true });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.unmount();
      this.abort.abort();
    }
    mount() {
      this.unmount();
      const template = this.querySelector('template');
      if (!template) return;
      this.mountAbort = new AbortController();
      const options = { signal: this.mountAbort.signal };
      const trigger = this.dataset.trigger || 'shop';
      ['desktop', 'mobile'].forEach(mode => {
        const id = `${mode === 'desktop' ? 'HeaderMenu' : 'HeaderDrawer'}-${trigger}`;
        const original = document.getElementById(id);
        // Fail safely if a future theme changes its navigation structure.
        if (!original || original.tagName !== 'A') return;
        const root = document.createElement('div');
        root.className = `hn-im hn-im--${mode}`;
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `${id}-image-toggle`;
        button.className = `${original.className} hn-im__toggle`;
        button.setAttribute('aria-expanded', 'false');
        const label = document.createElement('span');
        label.textContent = original.textContent.trim();
        if (original.hasAttribute('aria-current')) label.className = 'header__active-menu-item';
        button.append(label);
        const caret = document.createElement('span');
        caret.className = 'hn-im__caret';
        caret.setAttribute('aria-hidden', 'true');
        button.append(caret);
        const panel = document.createElement('div');
        panel.className = 'hn-im__panel';
        panel.id = `HNImageMenu-${this.dataset.section}-${mode}`;
        panel.hidden = true;
        panel.setAttribute('aria-labelledby', button.id);
        panel.append(template.content.cloneNode(true));
        // Desktop and mobile show the same content but must not duplicate editor identifiers.
        if (mode === 'mobile') panel.querySelectorAll('[data-shopify-editor-block]').forEach(el => el.removeAttribute('data-shopify-editor-block'));
        button.setAttribute('aria-controls', panel.id);
        root.append(button, panel);
        original.replaceWith(root);
        const m = { root, button, panel, original, mode, open: false, openTimer: 0, closeTimer: 0 };
        this.mounts.push(m);
        button.addEventListener('click', () => {
          if (m.open && m.openedBy === 'hover') m.openedBy = 'click';
          else if (m.open) this.close(m);
          else this.open(m, 'click');
        }, options);
        button.addEventListener('keydown', event => {
          if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          this.open(m, 'keyboard');
          const links = panel.querySelectorAll('a[href]');
          (event.key === 'ArrowUp' ? links[links.length - 1] : links[0])?.focus();
        }, options);
        root.addEventListener('keydown', event => {
          if (event.key !== 'Escape' || !m.open) return;
          event.preventDefault();
          event.stopPropagation();
          m.eatEscape = true;
          this.close(m, true);
        }, options);
        root.addEventListener('keyup', event => {
          if (event.key === 'Escape' && m.eatEscape) {
            event.stopPropagation();
            m.eatEscape = false;
          }
        }, options);
        root.addEventListener('focusout', () => {
          setTimeout(() => {
            if (root.isConnected && !root.contains(document.activeElement)) this.close(m);
          }, 0);
        }, options);
        panel.addEventListener('click', event => {
          if (event.target.closest('a[href]')) this.close(m);
        }, options);
        if (mode === 'desktop') {
          root.addEventListener('pointerenter', () => {
            if (!this.hoverMedia.matches || !this.media.matches) return;
            clearTimeout(m.closeTimer);
            m.openTimer = setTimeout(() => this.open(m, 'hover'), 120);
          }, options);
          root.addEventListener('pointerleave', () => {
            clearTimeout(m.openTimer);
            m.closeTimer = setTimeout(() => {
              if (!root.contains(document.activeElement)) this.close(m);
            }, 280);
          }, options);
        } else {
          const drawer = root.closest('header-drawer > details');
          drawer?.addEventListener('toggle', () => { if (!drawer.open) this.close(m); }, options);
        }
      });
    }
    open(m, source) {
      clearTimeout(m.openTimer);
      clearTimeout(m.closeTimer);
      if ((m.mode === 'desktop') !== this.media.matches) return;
      if (m.open) return;
      this.mounts.forEach(other => { if (other !== m) this.close(other); });
      m.open = true;
      m.openedBy = source;
      m.panel.hidden = false;
      m.button.setAttribute('aria-expanded', 'true');
      m.root.classList.add('hn-im--open');
      if (m.mode === 'desktop') {
        m.sticky = m.root.closest('sticky-header');
        if (m.sticky) { m.oldPreventHide = m.sticky.preventHide; m.sticky.preventHide = true; }
        this.position(m);
      }
    }
    close(m, restoreFocus = false) {
      clearTimeout(m.openTimer);
      clearTimeout(m.closeTimer);
      if (!m.open) return;
      m.open = false;
      m.panel.hidden = true;
      m.root.classList.remove('hn-im--open');
      m.button.setAttribute('aria-expanded', 'false');
      if (m.sticky) { m.sticky.preventHide = m.oldPreventHide; m.sticky = null; }
      if (restoreFocus) m.button.focus();
    }
    position(m) {
      const header = m.root.closest('.header-wrapper');
      if (!header) return;
      const rootRect = m.root.getBoundingClientRect();
      const bottom = header.getBoundingClientRect().bottom;
      // Align the panel with the viewport, not the width of the Shop label.
      m.panel.style.left = `${-rootRect.left}px`;
      m.panel.style.top = `${bottom - rootRect.top}px`;
      m.panel.style.width = `${document.documentElement.clientWidth}px`;
      m.panel.style.maxHeight = `${Math.max(120, window.innerHeight - bottom - 16)}px`;
    }
    positionOpen() {
      this.mounts.forEach(m => { if (m.open && m.mode === 'desktop') this.position(m); });
    }
    revealInEditor() {
      const m = this.mounts.find(item => item.mode === (this.media.matches ? 'desktop' : 'mobile'));
      if (!m) return;
      if (m.mode === 'mobile') {
        const drawer = m.root.closest('header-drawer');
        const summary = drawer?.querySelector(':scope > details > summary');
        if (summary && !summary.parentElement.open) summary.click();
      }
      this.open(m, 'editor');
    }
    unmount() {
      this.mounts?.forEach(m => {
        this.close(m);
        if (m.root.isConnected) m.root.replaceWith(m.original);
      });
      this.mounts = [];
      this.mountAbort?.abort();
    }
  }
  customElements.define('hn-image-menu', HNImageMenu);
})();
